import { useState, useMemo, useEffect } from 'react';
import { Icon, IconDefinitions } from '../../../../components';
import { Badge } from 'react-bootstrap';
import { UploadSessionModel } from '../../../../data/entities';
import { Pager, PageChangeEvent } from '@progress/kendo-react-data-tools';
import { FilterToggle } from '../../../../components/filterToggle';
import { UploadResultCard } from './UploadResultCard';
import { getSessionAlertLevel, AlertLevel } from '../../helper';

const PAGE_SIZE = 5;
type HistoryFilter = 'all' | AlertLevel;

interface Props {
  historySessions: UploadSessionModel[];
  onViewResults: (session: UploadSessionModel) => void;
}

export function InspectionHistorySection({ historySessions, onViewResults }: Props) {
  const [skip, setSkip] = useState(0);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('all');

  const statusCounts = useMemo(() => {
    const counts: Record<AlertLevel, number> = { Passed: 0, Warning: 0, Alert: 0, Error: 0 };
    historySessions.forEach((s) => {
      counts[getSessionAlertLevel(s)]++;
    });
    return counts;
  }, [historySessions]);

  const auditTrail = useMemo(() => {
    const mapped = historySessions.map((s: UploadSessionModel) => ({
      session: s,
      status: getSessionAlertLevel(s),
    }));
    if (historyFilter === 'all') return mapped;
    return mapped.filter((item) => item.status === historyFilter);
  }, [historySessions, historyFilter]);

  const pagedAuditTrail = auditTrail.slice(skip, skip + PAGE_SIZE);

  // Reset to first page when data shrinks below current skip
  useEffect(() => {
    if (skip >= auditTrail.length && auditTrail.length > 0) setSkip(0);
  }, [auditTrail.length]);

  const handlePageChange = (e: PageChangeEvent) => setSkip(e.skip);

  const handleFilterChange = (filter: HistoryFilter) => {
    setHistoryFilter(filter);
    setSkip(0);
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <h6 className="mb-0">
            <Icon iconName={IconDefinitions.clock} className="me-2 text-muted" />
            Inspection History
          </h6>
          {auditTrail.length > 0 && (
            <Badge bg="secondary" className="ms-2">
              {auditTrail.length}
            </Badge>
          )}
        </div>

        <FilterToggle
          options={[
            { key: 'all' as HistoryFilter, label: 'All', count: statusCounts.Passed + statusCounts.Warning + statusCounts.Alert + statusCounts.Error },
            { key: 'Passed' as HistoryFilter, label: 'Passed', count: statusCounts.Passed },
            { key: 'Warning' as HistoryFilter, label: 'Warning', count: statusCounts.Warning },
            { key: 'Alert' as HistoryFilter, label: 'Alert', count: statusCounts.Alert },
            { key: 'Error' as HistoryFilter, label: 'Error', count: statusCounts.Error },
          ]}
          activeFilter={historyFilter}
          onChange={handleFilterChange}
        />
      </div>

      {auditTrail.length === 0 ? (
        <div className="text-center py-5">
          <Icon
            iconName={
              historyFilter === 'Passed' ? IconDefinitions.circleCheck
              : historyFilter === 'Warning' ? IconDefinitions.triangleExclamation
              : historyFilter === 'Alert' ? IconDefinitions.circleExclamation
              : historyFilter === 'Error' ? IconDefinitions.circleXmark
              : IconDefinitions.clock
            }
            className={
              historyFilter === 'Passed' ? 'text-success mb-3'
              : historyFilter === 'Warning' ? 'text-warning mb-3'
              : historyFilter === 'Alert' ? 'text-alert mb-3'
              : historyFilter === 'Error' ? 'text-danger mb-3'
              : 'text-muted mb-3'
            }
            style={{ fontSize: '2.5rem' }}
          />
          <p className="fw-semibold text-dark mb-1">
            {historyFilter === 'all' ? 'No upload history' : `No ${historyFilter.toLowerCase()} uploads`}
          </p>
          <p className="text-muted small mb-0">
            {historyFilter === 'all'
              ? 'Completed uploads will appear here.'
              : `There are no uploads with a ${historyFilter.toLowerCase()} status.`}
          </p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-column gap-3">
            {pagedAuditTrail.map(({ session, status }) => (
              <UploadResultCard
                key={session.id}
                session={session}
                status={status}
                onViewResults={status !== 'Error' ? onViewResults : undefined}
              />
            ))}
          </div>

          <Pager
            skip={skip}
            take={PAGE_SIZE}
            total={auditTrail.length}
            onPageChange={handlePageChange}
            className="border-top-0 mt-3"
          />
        </>
      )}
    </div>
  );
}